import { useEffect, useState, forwardRef, MouseEvent, KeyboardEvent } from 'react';
import classNames from 'classnames';

import fetchWithTimeout from '../../../utils/fetchWithTimeout';
import { isDevelopment } from '../../../utils';

import svgIconsData from './svgIcons';
import useSvgIconStyles from './styles';
import ISvgIcon, { SvgIconOption } from './types';

const iconsCache = new Map();

export function SvgIcon({
  name,
  url,
  onClick,
  onKeyDown,
  className = '',
  role = 'presentation',
  ariaHidden,
  tabIndex,
  mRef,
  ...props
}: ISvgIcon) {
  const [svg, setSvg] = useState('');
  const classes = useSvgIconStyles();

  if (isDevelopment()) {
    console.warn(`SVGIcon is deprecated! Please use cascade icons.`);
  }

  useEffect(() => {
    let raceCondition = false;
    (async () => {
      const iconUrl = url || svgIconsData[name as SvgIconOption];
      if (!iconUrl) {
        isDevelopment() && console.warn(`There isn't any icon with this '${name}' name`);
        return;
      }

      if (!iconsCache.has(iconUrl)) {
        iconsCache.set(iconUrl, fetchWithTimeout(iconUrl));
      }
      const res = await iconsCache.get(iconUrl);
      const svgText = await res.clone().text();
      if (!raceCondition) {
        setSvg(svgText);
      }
    })();
    return () => {
      raceCondition = true;
    };
  }, [name, url]);

  const handleIconClick = (e?: MouseEvent) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleIconKeyDown = (e?: KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
    if (e.key === 'Enter' && onClick) {
      onClick();
    }
  };

  return (
    <i
      className={classNames(classes.root, 'bg-svg-icon', className)}
      onClick={handleIconClick}
      role={role}
      onKeyDown={handleIconKeyDown}
      aria-hidden={ariaHidden}
      tabIndex={tabIndex}
      ref={mRef}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
      {...props}
    />
  );
}

const SvgIconWithForwardedRef = forwardRef<HTMLDivElement, ISvgIcon>((props, ref) => (
  <SvgIcon {...props} mRef={ref} />
));
SvgIconWithForwardedRef.displayName = 'SvgIcon';

export default SvgIconWithForwardedRef;
